import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect } from "react";
import {
  type Control,
  type FieldErrors,
  type UseFormSetValue,
  useFieldArray,
  useWatch,
} from "react-hook-form";

import { useActorOptions } from "Actors/Api/Queries/useActorOptions";
import { useClientOptions } from "Clients/Api/Queries/useClientOptions";
import { BlFormCheckbox } from "Forms/Components/BlFormCheckbox";
import { BlFormDatePicker } from "Forms/Components/BlFormDatePicker";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import {
  getCountryOptions,
  getIndustryOptions,
  getLanguageOptions,
} from "Forms/Utils/EnumOptions";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useModuleOptions } from "Modules/Api/Queries/useModuleOptions";
import type { CreateProjectFormData } from "Projects/Forms/createProjectFormSchema";
import type { BlOption } from "Shared/Inputs/BlOption";

interface ReadOnlyExclusiveContract {
  actorName: string;
  price: number;
  jobLimit: number;
}

interface ProjectFormProps {
  control: Control<CreateProjectFormData>;
  errors: FieldErrors<CreateProjectFormData>;
  autoSelectFirstModule?: boolean;
  setValue?: UseFormSetValue<CreateProjectFormData>;
  disableModule?: boolean;
  /** Override for the module dropdown options (used in edit mode to keep the
   * current module visible even if it's not in the active modules list). */
  moduleOptionsOverride?: BlOption[];
  /** When provided, exclusive contracts are shown as read-only (edit mode). */
  readOnlyExclusiveContracts?: ReadOnlyExclusiveContract[];
}

export const ProjectForm = ({
  control,
  errors,
  autoSelectFirstModule,
  setValue,
  disableModule,
  moduleOptionsOverride,
  readOnlyExclusiveContracts,
}: ProjectFormProps): React.ReactNode => {
  const { t } = useResource();
  const theme = useTheme();
  const fetchedModuleOptions = useModuleOptions();
  const moduleOptions = moduleOptionsOverride ?? fetchedModuleOptions;
  const clientOptions = useClientOptions();
  const actorOptions = useActorOptions();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "exclusiveContracts",
  });

  const hasExclusiveContracts = useWatch({
    control,
    name: "hasExclusiveContracts",
  });

  useEffect(() => {
    if (autoSelectFirstModule && setValue && moduleOptions.length > 0) {
      setValue("moduleID", moduleOptions[0].value as string);
    }
  }, [autoSelectFirstModule, moduleOptions, setValue]);

  return (
    <Grid container spacing={Spacing.M}>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="moduleID"
          label={t(Resources.Projects.CreateProjectModal.SelectModule)}
          options={moduleOptions}
          errors={errors}
          placeholder={t(
            Resources.Projects.CreateProjectModal.SelectModulePlaceholder,
          )}
          required
          disabled={disableModule}
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="industry"
          label={t(Resources.Projects.CreateProjectModal.Industry)}
          options={getIndustryOptions()}
          errors={errors}
          placeholder={t(
            Resources.Projects.CreateProjectModal.SelectIndustryPlaceholder,
          )}
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="language"
          label={t(Resources.Projects.CreateProjectModal.Language)}
          options={getLanguageOptions()}
          errors={errors}
          placeholder={t(
            Resources.Projects.CreateProjectModal.SelectLanguagePlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="licensedCountries"
          label={t(Resources.Projects.CreateProjectModal.LicensedCountries)}
          options={getCountryOptions()}
          errors={errors}
          placeholder={t(
            Resources.Projects.CreateProjectModal
              .SelectLicensedCountriesPlaceholder,
          )}
          multiple={true}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="name"
          label={t(Resources.Projects.CreateProjectModal.Name)}
          errors={errors}
          placeholder={t(Resources.Projects.CreateProjectModal.NamePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="clientID"
          label={t(Resources.Projects.CreateProjectModal.Client)}
          options={clientOptions}
          errors={errors}
          placeholder={t(
            Resources.Projects.CreateProjectModal.SelectClientPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDatePicker
          control={control}
          name="startDate"
          label={t(Resources.Projects.CreateProjectModal.StartDate)}
          errors={errors}
        />
      </Grid>
      <Grid size={6}>
        <BlFormDatePicker
          control={control}
          name="endDate"
          label={t(Resources.Projects.CreateProjectModal.EndDate)}
          errors={errors}
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="jobsLimit"
          label={t(Resources.Projects.CreateProjectModal.JobsLimit)}
          isNumber
          errors={errors}
          placeholder={t(
            Resources.Projects.CreateProjectModal.JobsLimitPlaceholder,
          )}
        />
      </Grid>

      {readOnlyExclusiveContracts && readOnlyExclusiveContracts.length > 0 && (
        <>
          <Grid size={12}>
            <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.7 }}>
              {t(Resources.Projects.CreateProjectModal.HasExclusiveContracts)}
            </Typography>
          </Grid>
          {readOnlyExclusiveContracts.map((ec, index) => (
            <Grid size={12} key={ec.actorName}>
              {index > 0 && (
                <Divider
                  sx={{ mb: Spacing.M, borderColor: theme.colors.border }}
                />
              )}
              <Grid container spacing={Spacing.M}>
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t(Resources.Projects.CreateProjectModal.ContractedActor)}
                  </Typography>
                  <Typography variant="body1">{ec.actorName}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t(Resources.Projects.CreateProjectModal.ContractPrice)}
                  </Typography>
                  <Typography variant="body1">{ec.price}</Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t(Resources.Projects.CreateProjectModal.ContractJobsLimit)}
                  </Typography>
                  <Typography variant="body1">{ec.jobLimit}</Typography>
                </Grid>
              </Grid>
            </Grid>
          ))}
        </>
      )}

      {!readOnlyExclusiveContracts && (
        <>
          <Grid size={12}>
            <BlFormCheckbox
              control={control}
              name="hasExclusiveContracts"
              errors={errors}
              label={t(
                Resources.Projects.CreateProjectModal.HasExclusiveContracts,
              )}
              onChange={(checked) => {
                if (checked && fields.length === 0) {
                  append({ actorID: "", price: 0, jobLimit: 0 });
                }
              }}
            />
          </Grid>

          {hasExclusiveContracts && (
            <>
              {fields.map((field, index) => (
                <Grid size={12} key={field.id}>
                  {index > 0 && (
                    <Divider
                      sx={{
                        mb: Spacing.M,
                        borderColor: theme.colors.border,
                      }}
                    />
                  )}
                  <Grid container spacing={Spacing.M}>
                    <Grid size={11}>
                      <BlFormDropdown
                        control={control}
                        name={`exclusiveContracts.${index}.actorID`}
                        label={t(
                          Resources.Projects.CreateProjectModal.ContractedActor,
                        )}
                        options={actorOptions}
                        errors={errors}
                        required
                      />
                    </Grid>
                    <Grid
                      size={1}
                      sx={{
                        display: "flex",
                        alignItems: "flex-end",
                        pb: 0.5,
                      }}
                    >
                      <IconButton
                        onClick={() => remove(index)}
                        size="small"
                        sx={{ color: theme.palette.error.main }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        name={`exclusiveContracts.${index}.price`}
                        label={t(
                          Resources.Projects.CreateProjectModal.ContractPrice,
                        )}
                        isNumber
                        errors={errors}
                        required
                      />
                    </Grid>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        name={`exclusiveContracts.${index}.jobLimit`}
                        label={t(
                          Resources.Projects.CreateProjectModal
                            .ContractJobsLimit,
                        )}
                        isNumber
                        errors={errors}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))}

              <Grid size={12}>
                <Box
                  onClick={() => append({ actorID: "", price: 0, jobLimit: 0 })}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    color: theme.palette.primary.main,
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  <AddIcon fontSize="small" />
                  <Typography variant="body2" fontWeight={600}>
                    {t(
                      Resources.Projects.CreateProjectModal
                        .AddExclusiveContract,
                    )}
                  </Typography>
                </Box>
              </Grid>
            </>
          )}
        </>
      )}
    </Grid>
  );
};
